import React, { useEffect, useMemo, useState } from "react";
import { HeaderSection } from "@cpnw/ui";
import { AccountContainer } from "hooks/useAccount";
import DashboardPageTabs from "components/DashboardPageTabs";
import EdSummary from "./EdSummary";
import EdOIGSAMCheck from "./EdOIGSAMCheck";
import HcPassport from "./HcPassport";
import HcOIGSAMCheck from "./HcOIGSAMCheck";
import HcClinicalRoster from "./HcClinicalRoster";
import styles from "./Reports.module.css";
import { CanPermissions } from "pages/enums";

const Reports = () => {
  const { userRoles, hasCanPermissions, conAssignments } =
    AccountContainer.useContainer();

  const [activeTab, setActiveTab] = useState("");
  const isHealthcare = userRoles.includes("HEALTHCARE");
  const isEducationOrFaculty =
    userRoles.includes("EDUCATION") || userRoles.includes("FACULTY");

  const permissions = useMemo(
    () =>
      Array.from(new Set(conAssignments.flatMap((a) => a.Permissions || []))),
    [conAssignments],
  );

  const canViewOIGSAMCheck = useMemo(
    () =>
      hasCanPermissions(CanPermissions.CanHIPAA) ||
      (isEducationOrFaculty &&
        (permissions.includes("EducationOIGSAMCheckView") ||
          permissions.includes("EducationOIGSAMCheckEdit"))) ||
      (isHealthcare &&
        (permissions.includes("HealthcareOIGSAMCheckView") ||
          permissions.includes("HealthcareOIGSAMCheckEdit"))),
    [hasCanPermissions, isEducationOrFaculty, isHealthcare, permissions],
  );

  const canViewClinicalRoster = isHealthcare;

  const tabs = useMemo(() => {
    const availableTabs = [];

    if (isHealthcare || isEducationOrFaculty) {
      availableTabs.push(Tabs.HcPassport);
    }

    if (canViewOIGSAMCheck && (isHealthcare || isEducationOrFaculty)) {
      availableTabs.push(isHealthcare ? Tabs.HcOIGSAMCheck : Tabs.EdOIGSAMCheck);
    }

    if (canViewClinicalRoster) {
      availableTabs.push(Tabs.HcClinicalRoster);
    }

    return availableTabs;
  }, [
    canViewClinicalRoster,
    canViewOIGSAMCheck,
    isEducationOrFaculty,
    isHealthcare,
  ]);

  useEffect(() => {
    if (tabs.length === 0) {
      setActiveTab("");
      return;
    }

    if (tabs.length > 0 && !tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
  }, [activeTab, tabs]);

  return (
    <section>
      <HeaderSection header={"Reports"} />
      <div className={styles.pageTabContaner}>
        <DashboardPageTabs
          titleList={tabs}
          activeTitle={activeTab}
          onChange={(tab) => setActiveTab(tab)}
        />
      </div>

      <div className={styles.tableContainer}>
        {activeTab === Tabs.EdSummary && <EdSummary />}
        {activeTab === Tabs.HcPassport && <HcPassport userRoles={userRoles} />}
        {activeTab === Tabs.EdOIGSAMCheck && isEducationOrFaculty && <EdOIGSAMCheck />}
        {activeTab === Tabs.HcOIGSAMCheck && isHealthcare && <HcOIGSAMCheck />}
        {activeTab === Tabs.HcClinicalRoster && <HcClinicalRoster />}
      </div>
    </section>
  );
};

const Tabs = {
  EdSummary: "Summary", // Deprecated: beta routes Education/Faculty through Passport.
  EdOIGSAMCheck: "OIG/SAM Check",
  HcPassport: "Passport",
  HcOIGSAMCheck: "OIG/SAM Check",
  HcClinicalRoster: "Clinical Roster",
};

export default Reports;
